import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { trackingPublicConfigQueryOptions } from "~/lib/tracking/config";
import { isChoiceCurrent, readStoredConsent, saveConsent } from "~/lib/tracking/consent";

import type { StoredConsent } from "~/lib/tracking/consent";

const BUTTON_CLASS =
  "mt-1 inline-flex h-10 items-center rounded-[8px] border border-landing-text/70 px-4 text-[14px] font-semibold text-landing-text transition-colors hover:bg-landing-stone-100 disabled:opacity-60";

/** Texto del botón. Lo cita la política de privacidad: un solo lugar. */
export const OPT_OUT_LABEL = "No usar herramientas de medición ni publicidad en este navegador";

/**
 * «No usar herramientas de medición ni publicidad en este navegador» de /privacy. Guarda una elección `{analytics: false, ads: false}`
 * de la versión vigente (cookie `acd_consent` + fila de auditoría con origen `privacy`): a partir
 * de ahí no se cargan las herramientas de medición ni de publicidad y el servidor no les avisa de
 * los registros, inscripciones y compras. Funciona con el aviso de cookies apagado o prendido.
 *
 * El registro propio de visitas (server.js + VisitBeacon) NO mira esta elección: por eso el texto
 * habla de «herramientas de medición y publicidad» y no de «no medir» a secas.
 *
 * La cookie se lee en un efecto (nunca en el inicializador de `useState`): en SSR no hay
 * `document` y el primer render del cliente tiene que coincidir con el del servidor.
 */
export function MeasurementOptOut() {
  const { data: config } = useQuery(trackingPublicConfigQueryOptions);
  const version = config?.consentVersion;
  const [stored, setStored] = useState<StoredConsent | null>(null);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justOptedOut, setJustOptedOut] = useState(false);

  useEffect(() => {
    setStored(readStoredConsent());
    setReady(true);
  }, []);

  // Sin config (API caída) no sabemos la versión vigente: mejor no mostrar nada que guardar mal.
  if (!version || !ready) return null;

  const optedOut =
    isChoiceCurrent(stored, version) && stored !== null && !stored.analytics && !stored.ads;

  const choose = async (measure: boolean) => {
    setSaving(true);
    try {
      await saveConsent({ analytics: measure, ads: measure }, version, "privacy");
      // saveConsent deja la cookie (la API o, si no contesta, el respaldo local).
      setStored(
        readStoredConsent() ?? {
          analytics: measure,
          ads: measure,
          version,
          at: Math.floor(Date.now() / 1000),
        },
      );
      setJustOptedOut(!measure);
    } finally {
      setSaving(false);
    }
  };

  if (optedOut) {
    return (
      <div data-testid="privacy-measurement-optout" className="flex flex-col items-start gap-2">
        <p role="status" className="text-[15px] text-landing-text">
          {justOptedOut
            ? "Listo: en este navegador ya no usamos herramientas de medición ni publicidad."
            : "Este navegador está excluido de las herramientas de medición y publicidad."}
        </p>
        <button
          type="button"
          disabled={saving}
          onClick={() => void choose(true)}
          className={BUTTON_CLASS}
        >
          Volver a permitir la medición y la publicidad
        </button>
      </div>
    );
  }

  return (
    <div data-testid="privacy-measurement-optout" className="flex flex-col items-start gap-2">
      <button
        type="button"
        disabled={saving}
        onClick={() => void choose(false)}
        className={BUTTON_CLASS}
      >
        {OPT_OUT_LABEL}
      </button>
    </div>
  );
}
